import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bookmark, Search, Plus, X, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function CreateKeepPO() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const initialItemsRef = useRef(null);
  const [quotas, setQuotas] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState({}); // quota_id -> {variant_id, name, batch, remaining, qty, price}
  const [cust, setCust] = useState({ id: "", name: "", phone: "", group_id: "", group_name: "" });
  const [custQ, setCustQ] = useState("");
  const [custRes, setCustRes] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [marketplace, setMarketplace] = useState("");
  const [orderId, setOrderId] = useState("");
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data)).catch(() => {});
    api.get("/settings/marketplace-channels").then((r) => setChannels(r.data.channels || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/keep/${editId}`).then((r) => {
      const k = r.data;
      if (k.sales_bucket !== "PRE_ORDER") { toast.error("Keep ini bukan Pre-Order"); navigate(`/sales/keep/edit/${editId}`); return; }
      initialItemsRef.current = k.items || [];
      setCust({ id: k.customer_id || "", name: k.customer_name || "", phone: k.phone || "", group_id: k.group_id || "", group_name: k.group_name || "" });
      setNote(k.note || "");
      setGroupId(k.group_id || "");
      setMarketplace(k.marketplace || ""); setOrderId(k.order_id || "");
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); navigate("/sales/preorder-warehouse"); });
  }, [editId]);

  useEffect(() => {
    if (!groupId) { setQuotas([]); if (!initialItemsRef.current) setCart({}); return; }
    api.get("/preorders/quotas", { params: { status: "Open", group_id: groupId } }).then((r) => {
      const items = r.data.items || [];
      setQuotas(items);
      if (initialItemsRef.current) {
        const pending = initialItemsRef.current; initialItemsRef.current = null;
        const next = {};
        for (const it of pending) {
          const qid = it.po_quota_id;
          if (!qid) continue;
          const match = items.find((x) => x.id === qid);
          const ownQty = it.qty_remaining ?? it.qty ?? 0;
          next[qid] = {
            variant_id: it.variant_id,
            name: match ? `${match.product_name} ${match.color}/${match.size}` : `${it.product_name || ""} ${it.color || ""}/${it.size || ""}`.trim(),
            batch: match ? (match.period_label || "-") : "-",
            remaining: (match ? match.remaining_po : 0) + ownQty,
            qty: ownQty,
            price: it.price || 0,
          };
        }
        setCart(next);
      } else {
        setCart({});
      }
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [groupId]);
  useEffect(() => { if (custQ.trim().length < 1) { setCustRes([]); return; } const t = setTimeout(() => api.get("/customers", { params: { q: custQ } }).then((r) => setCustRes(r.data.slice(0, 8))).catch(() => {}), 250); return () => clearTimeout(t); }, [custQ]);

  const ql = q.toLowerCase();
  const options = quotas.filter((x) => x.remaining_po > 0 && (x.product_name + x.color + x.size + (x.period_label || "")).toLowerCase().includes(ql));

  const add = (x) => setCart((c) => {
    const cur = c[x.id];
    const nextQty = Math.min((cur?.qty || 0) + 1, x.remaining_po);
    return { ...c, [x.id]: { variant_id: x.variant_id, name: `${x.product_name} ${x.color}/${x.size}`, batch: x.period_label || "-", remaining: x.remaining_po, qty: nextQty, price: cur?.price ?? (x.selling_price || 0) } };
  });
  const setLine = (id, k, v) => setCart((c) => ({ ...c, [id]: { ...c[id], [k]: v } }));
  const rm = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const lines = Object.entries(cart);
  const total = lines.reduce((s, [, l]) => s + (parseInt(l.price) || 0) * (parseInt(l.qty) || 0), 0);
  const grpNameSel = groups.find((g) => g.id === groupId)?.name || cust.group_name || "";
  const isOnline = grpNameSel.toLowerCase() === "online";
  const mktOptions = Array.from(new Set([...channels, marketplace].filter(Boolean)));

  const save = async () => {
    const items = lines.map(([qid, l]) => ({ variant_id: l.variant_id, po_quota_id: qid, qty: parseInt(l.qty) || 0, price: parseInt(l.price) || 0 })).filter((i) => i.qty > 0);
    if (!groupId) return toast.error("Pilih customer group dulu");
    if (!items.length) return toast.error("Tambahkan minimal 1 barang PO");
    if (!cust.name.trim()) return toast.error("Nama customer wajib");
    for (const [, l] of lines) { if ((parseInt(l.qty) || 0) > l.remaining) return toast.error(`Qty ${l.name} melebihi sisa kuota (${l.remaining})`); }
    if (isOnline && !marketplace) return toast.error("Pilih Marketplace untuk Group Online");
    setSaving(true);
    try {
      const grpName = groups.find((g) => g.id === groupId)?.name || "";
      const payload = { sales_bucket: "PRE_ORDER", group_id: groupId, group_name: grpName, customer_id: cust.id || null, customer_name: cust.name, phone: cust.phone, keep_type: "Regular", channel: "Manual", marketplace: marketplace || "", order_id: orderId || "", note, items };
      const r = isEdit ? await api.put(`/keep/${editId}`, payload) : await api.post("/keep", payload);
      toast.success(`Keep PO ${r.data.number || ""} ${isEdit ? "diperbarui" : "dibuat"}`);
      navigate("/sales/preorder-warehouse");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="create-keep-po-page">
      <PageHeader title={isEdit ? "Edit Keep PO" : "Create Keep PO"} subtitle="Keep khusus Pre-Order (bucket PRE_ORDER, memakai Sisa Kuota PO — tidak memotong stok fisik)" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 space-y-4">
          <Panel className="p-4 space-y-2">
            <Label className="text-cocoa">Customer</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative col-span-2">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-wmuted" />
                <Input className="pl-8 bg-white" placeholder="Cari customer terdaftar (nama/HP)…" value={custQ} onChange={(e) => setCustQ(e.target.value)} data-testid="ckpo-cust-search" />
                {custRes.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 border border-taupe/30 rounded-lg bg-white max-h-52 overflow-y-auto shadow">
                    {custRes.map((c) => (
                      <button key={c.id} type="button" onClick={() => { setCust({ id: c.id, name: c.name, phone: c.phone || "", group_id: c.group_id || "", group_name: c.group_name || "", marketplace: c.marketplace || "" }); if (c.group_id) setGroupId(c.group_id); if (c.marketplace) setMarketplace(c.marketplace); setCustQ(""); setCustRes([]); }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-ivory" data-testid={`ckpo-cust-opt-${c.id}`}>{c.name} <span className="text-xs text-wmuted">{c.phone}</span></button>
                    ))}
                  </div>
                )}
              </div>
              <Input placeholder="Nama customer *" value={cust.name} onChange={(e) => setCust({ ...cust, id: "", name: e.target.value })} data-testid="ckpo-cust-name" />
              <Input placeholder="No. HP / WhatsApp" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} data-testid="ckpo-cust-phone" />
            </div>
            {(
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1" data-testid="ckpo-marketplace-fields">
                <div>
                  <Label className="text-cocoa">Marketplace</Label>
                  <Select value={marketplace || ""} onValueChange={setMarketplace}>
                    <SelectTrigger className="bg-white" data-testid="ckpo-marketplace-select"><SelectValue placeholder="Select Marketplace" /></SelectTrigger>
                    <SelectContent>{mktOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-cocoa">Order ID Marketplace</Label><Input className="bg-white" placeholder="Enter Order ID" value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="ckpo-order-id-input" /></div>
              </div>
            )}
          </Panel>
          <Panel className="p-4 space-y-2">
            <Label className="text-cocoa">Customer Group *</Label>
            <Select value={groupId} onValueChange={setGroupId} disabled={!!cust.id && !!cust.group_id}>
              <SelectTrigger className="bg-white" data-testid="ckpo-group"><SelectValue placeholder="Pilih customer group" /></SelectTrigger>
              <SelectContent className="max-h-72">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
            {(!!cust.id && !!cust.group_id)
              ? <p className="text-xs text-cocoa" data-testid="ckpo-group-locked">Group terkunci mengikuti customer <b>{cust.name}</b> (<b>{cust.group_name || groups.find((g) => g.id === groupId)?.name}</b>) agar batch antar-channel tidak tumpang tindih.</p>
              : <p className="text-xs text-wmuted">Ketersediaan stok PO mengikuti batch Open PO untuk group ini.</p>}
          </Panel>
          <Panel className="p-4 space-y-2">
            <Label className="text-cocoa">Pilih Barang PO (kuota Open)</Label>
            <div className="relative"><Search className="w-4 h-4 absolute left-2 top-2.5 text-wmuted" /><Input className="pl-8 bg-white" placeholder="Cari produk / warna / batch…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ckpo-item-search" /></div>
            <div className="border border-taupe/20 rounded-lg max-h-72 overflow-y-auto divide-y divide-taupe/10">
              {options.map((x) => (
                <div key={x.id} className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-ivory">
                  <span>{x.product_name} <span className="text-wmuted text-xs">{x.color}/{x.size}</span> <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cocoa/10 text-cocoa ml-1">{x.period_label || "PO"}</span> <span className="text-xs text-emerald-600 ml-1">sisa {x.remaining_po}</span></span>
                  <Button size="sm" variant="ghost" className="h-7 text-cocoa" onClick={() => add(x)} data-testid={`ckpo-add-${x.id}`}><Plus className="w-4 h-4" /></Button>
                </div>
              ))}
              {!options.length && <div className="px-3 py-6 text-center text-sm text-wmuted"><Package className="w-5 h-5 mx-auto mb-1 opacity-50" />{!groupId ? "Pilih customer group dulu untuk melihat ketersediaan stok PO." : "Belum ada kuota PO (Open) untuk group ini."}</div>}
            </div>
          </Panel>
        </div>
        <div className="lg:col-span-2">
          <Panel className="p-4 space-y-3" data-testid="ckpo-cart">
            <Label className="text-cocoa">Keranjang Keep PO</Label>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {lines.map(([id, l]) => (
                <div key={id} className="flex items-center gap-2 text-sm border-b border-taupe/10 pb-1.5" data-testid={`ckpo-line-${id}`}>
                  <div className="flex-1"><div>{l.name}</div><div className="text-[10px] text-wmuted">{l.batch} · sisa {l.remaining}</div></div>
                  <Input type="number" className="w-14 h-8 text-center" value={l.qty} onChange={(e) => setLine(id, "qty", Math.min(parseInt(e.target.value) || 0, l.remaining))} data-testid={`ckpo-qty-${id}`} />
                  <Input type="number" className="w-24 h-8" placeholder="Harga" value={l.price} onChange={(e) => setLine(id, "price", e.target.value)} data-testid={`ckpo-price-${id}`} />
                  <button onClick={() => rm(id)} className="text-status-reject"><X className="w-4 h-4" /></button>
                </div>
              ))}
              {!lines.length && <p className="text-sm text-wmuted text-center py-6"><Bookmark className="w-5 h-5 mx-auto mb-1 opacity-50" />Keranjang kosong</p>}
            </div>
            <div className="flex justify-between font-heading text-cocoa text-lg border-t border-taupe/20 pt-2"><span>Total</span><span data-testid="ckpo-total">{fmtIDR(total)}</span></div>
            <div><Label className="text-xs">Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="opsional" data-testid="ckpo-note" /></div>
            <Button onClick={save} disabled={saving || !lines.length} className="w-full bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="ckpo-save">{saving ? "Menyimpan…" : isEdit ? "Perbarui Keep PO" : "Simpan Keep PO"}</Button>
          </Panel>
        </div>
      </div>
    </div>
  );
}
